import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, 
    faEdit, 
    faFilePdf, 
    faTrash, 
    faEye, 
    faTimes,
    faChevronLeft,
    faChevronRight,
    faAnglesLeft,
    faAnglesRight
} from '@fortawesome/free-solid-svg-icons';
import { type Invoice, invoicesApi } from '../api';
import InvoicePreview from './InvoicePreview';
import BtnTaratic from '../../../components/ui/BtnTaratic';
import { useDeleteConfirmation } from '../../../components/ui/DeleteConfirmationProvider';

interface InvoiceListProps {
    onEditInvoice?: (invoice: Invoice) => void;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ onEditInvoice }) => {
    const { t } = useTranslation();
    const confirmDelete = useDeleteConfirmation();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const [inputPage, setInputPage] = useState('1');

    useEffect(() => {
        loadInvoices();
    }, []);

    // Reset pagination on filter/search change
    useEffect(() => {
        setPage(1);
        setInputPage('1');
    }, [filter, search]);

    const loadInvoices = () => {
        invoicesApi.getInvoices().then(setInvoices).catch(console.error);
    };

    const handleDelete = async (id: number) => {
        if (await confirmDelete({
            title: 'Eliminar borrador',
            message: t('invoices.dashboard.confirmDelete') || '¿Estás seguro de que deseas eliminar este borrador?',
        })) {
            try {
                await invoicesApi.deleteInvoice(id);
                loadInvoices();
            } catch (err) {
                console.error(err);
                alert('Error al eliminar');
            }
        }
    };

    const handleDownloadPdf = async (invoice: Invoice, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const blob = await invoicesApi.downloadInvoicePdf(invoice.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `factura_${invoice.invoice_number || 'borrador'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading PDF", err);
            alert('Error al descargar PDF');
        }
    };

    const filteredInvoices = invoices.filter(inv => {
        if (filter === 'drafts' && inv.status !== 'BORRADOR') return false;
        if (filter === 'issued' && inv.status !== 'EMITIDA') return false;

        if (search) {
            const searchLower = search.toLowerCase();
            const matchesNumber = inv.invoice_number?.toLowerCase().includes(searchLower);
            const matchesPatient = inv.patient_name?.toLowerCase().includes(searchLower);
            if (!matchesNumber && !matchesPatient) return false;
        }

        return true;
    });

    // Pagination computations
    const totalInvoices = filteredInvoices.length;
    const totalPages = Math.ceil(totalInvoices / perPage) || 1;
    const paginatedInvoices = filteredInvoices.slice((page - 1) * perPage, page * perPage);
    const startItem = totalInvoices === 0 ? 0 : (page - 1) * perPage + 1;
    const endItem = Math.min(page * perPage, totalInvoices);

    const handleJumpPage = (e: React.FormEvent) => {
        e.preventDefault();
        const p = parseInt(inputPage, 10);
        if (!isNaN(p) && p >= 1 && p <= totalPages) {
            setPage(p);
        } else {
            setInputPage(String(page));
        }
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('...');

            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);

            for (let i = start; i <= end; i++) pages.push(i);

            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const getStatusBadge = (status: string) => {
        if (status === 'BORRADOR') return <span className="px-3 py-1 bg-yellow-900/30 text-yellow-500 rounded-full text-xs font-bold border border-yellow-700/50">{t('invoices.dashboard.status.draft')}</span>;
        if (status === 'EMITIDA') return <span className="px-3 py-1 bg-green-900/30 text-green-500 rounded-full text-xs font-bold border border-green-700/50">{t('invoices.dashboard.status.issued')}</span>;
        return <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-xs font-bold border border-gray-600">{status}</span>;
    };

    return (
        <div data-ui="invoice-list-region-001" className="space-y-6">
            <div data-ui="invoice-list-region-002" className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0">
                <div data-ui="invoice-list-region-003" className="flex flex-wrap items-center gap-2 pt-2 pb-2">
                    {[
                        { id: 'all', label: t('invoices.dashboard.filters.all') },
                        { id: 'drafts', label: t('invoices.dashboard.filters.drafts') },
                        { id: 'issued', label: t('invoices.dashboard.filters.issued') },
                        { id: 'verified', label: t('invoices.dashboard.filters.verified') }
                    ].map(f => {
                        const isActive = filter === f.id;
                        let activeClasses = '';

                        if (isActive) {
                            if (f.id === 'all') activeClasses = '!bg-blue-500/20 text-blue-300 border-blue-500/30';
                            else activeClasses = '!bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
                        }

                        return (
                            <button
                                key={f.id}
                                data-ui={`invoice-filter-${f.id}`}
                                data-selected={isActive ? 'true' : 'false'}
                                onClick={() => setFilter(f.id)}
                                className={`btn-taratic px-5 py-2 !text-sm border ${isActive ? activeClasses : 'btn-ghost-dark !shadow-none hover:shadow-inner-glow hover:shadow-inner-glow-white-2xl hover:text-shadow-glow-white hover:scale-100'} whitespace-nowrap`}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
                <div data-ui="invoice-list-region-004" className="relative w-full md:w-1/3">
                    <div data-ui="invoice-list-region-005" className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faSearch} className="text-white/70" />
                    </div>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('invoices.dashboard.searchPlaceholder')}
                        className="w-full bg-black/20 border border-white/20 text-white text-sm rounded-full focus:ring-blue-400 focus:border-blue-400 block pl-11 p-2.5 placeholder-white/60 shadow-inner transition-all duration-300 hover:bg-black hover:shadow-inner-glow hover:shadow-inner-glow-white-2xl focus:bg-black focus:shadow-inner-glow focus:shadow-inner-glow-white-2xl"
                    />
                </div>
            </div>

            <div data-ui="invoice-list-region-006" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md overflow-hidden mb-10 flex flex-col">
                <div data-ui="invoice-list-region-007" className="overflow-x-auto gemini-scrollbar flex-1">
                    <table className="min-w-full divide-y divide-white/10">
                        <thead className="bg-black/75 border-b border-white/20">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.dashboard.table.invoiceNumber')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.dashboard.table.date')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.dashboard.table.status')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.dashboard.table.patient')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">VERIFACTU</th>
                                <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase tracking-wider">{t('invoices.dashboard.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                            {paginatedInvoices.map((invoice) => (
                                <tr key={invoice.id} className="hover:bg-black cursor-pointer transition-colors duration-200 group">
                                    <td className="px-4 py-4 whitespace-nowrap align-middle">
                                        <div data-ui="invoice-list-region-008" className="flex items-center space-x-2">
                                            <button
                                                className="btn-taratic btn-ghost-dark !p-2 flex items-center justify-center rounded-full w-8 h-8 hover:!bg-red-500/20 hover:!shadow-[inset_0_0_15px_rgba(239,68,68,0.5)] hover:!border-red-500/50 transition-all group"
                                                onClick={(e) => handleDownloadPdf(invoice, e)}
                                                title="Descargar PDF"
                                            >
                                                <FontAwesomeIcon icon={faFilePdf} className="text-red-500 group-hover:text-red-400 transition-colors" />
                                            </button>
                                            <div data-ui="invoice-list-region-009" className="text-sm font-semibold text-white">{invoice.invoice_number || `Borrador #${invoice.id}`}</div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap align-middle text-sm text-white/80">{new Date(invoice.issue_date).toLocaleDateString()}</td>
                                    <td className="px-4 py-4 whitespace-nowrap align-middle">{getStatusBadge(invoice.status)}</td>
                                    <td className="px-4 py-4 whitespace-nowrap align-middle text-sm text-white/80">{invoice.patient_name || '-'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap align-middle">
                                        {invoice.status === 'EMITIDA' ? (
                                            <span className="text-xs text-green-400 bg-green-900/20 px-2 py-1 rounded border border-green-700/50">Aceptado</span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium align-middle">
                                        <div data-ui="invoice-list-region-010" className="flex justify-end gap-2">
                                            <button
                                                className="btn-taratic btn-ghost-dark !p-2 flex items-center justify-center rounded-full w-8 h-8 hover:!bg-purple-500/20 hover:!shadow-[inset_0_0_15px_rgba(168,85,247,0.5)] hover:!border-purple-500/50 transition-all group"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (invoice.status === 'BORRADOR' && onEditInvoice) {
                                                        onEditInvoice(invoice);
                                                    } else {
                                                        setPreviewInvoice(invoice);
                                                    }
                                                }}
                                                title={invoice.status === 'BORRADOR' ? t('invoices.dashboard.table.edit', 'Editar') : t('invoices.dashboard.table.view', 'Ver')}
                                            >
                                                <FontAwesomeIcon icon={invoice.status === 'BORRADOR' ? faEdit : faEye} className="text-white group-hover:text-purple-400 transition-colors" />
                                            </button>
                                            {invoice.status === 'BORRADOR' && (
                                                <button
                                                    className="btn-taratic btn-ghost-dark !p-2 flex items-center justify-center rounded-full w-8 h-8 hover:!bg-red-500/20 hover:!shadow-[inset_0_0_15px_rgba(239,68,68,0.5)] hover:!border-red-500/50 transition-all group"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(invoice.id);
                                                    }}
                                                    title={t('invoices.dashboard.table.delete', 'Eliminar')}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="text-red-500 group-hover:text-red-400 transition-colors" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredInvoices.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-white/60 font-medium">
                                        {t('invoices.dashboard.noInvoices')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div data-ui="invoice-list-region-011" className="px-4 py-2.5 border-t border-white/20 bg-black/60 flex flex-col md:flex-row items-center justify-between gap-3 shrink-0">
                    <div data-ui="invoice-list-region-012" className="flex items-center gap-4 text-xs text-white/70 justify-center md:justify-start md:flex-1 w-full md:w-auto">
                        <span>
                            Mostrando <strong className="text-white font-medium">{startItem}</strong> a{' '}
                            <strong className="text-white font-medium">{endItem}</strong> de <strong className="text-white font-medium">{totalInvoices}</strong> facturas
                        </span>
                        <span className="hidden sm:inline-block text-white/40">|</span>
                        <span className="hidden sm:inline-block text-white/60">
                            Página <strong className="text-white font-medium">{page}</strong> de <strong className="text-white font-medium">{totalPages}</strong>
                        </span>
                    </div>

                    <div data-ui="invoice-list-region-013" className="flex items-center justify-center gap-3 flex-wrap sm:flex-nowrap md:flex-initial w-full md:w-auto">
                        {/* Direct Jump Input */}
                        <form onSubmit={handleJumpPage} className="flex items-center justify-center gap-1.5 text-xs text-white/70">
                            <span className="whitespace-nowrap">Ir a pág:</span>
                            <input
                                type="number"
                                min={1}
                                max={totalPages}
                                value={inputPage}
                                onChange={(e) => setInputPage(e.target.value)}
                                onBlur={() => {
                                    const p = parseInt(inputPage, 10);
                                    if (isNaN(p) || p < 1 || p > totalPages) {
                                        setInputPage(String(page));
                                    }
                                }}
                                className="w-12 sm:w-14 bg-black/20 border border-white/20 rounded-lg px-2 py-1 text-center text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            <BtnTaratic type="submit" color="ghost-dark" size="xs" className="px-2 py-1">
                                Ir
                            </BtnTaratic>
                        </form>

                        {/* Navigation Buttons */}
                        <div data-ui="invoice-list-region-014" className="flex items-center justify-center gap-1">
                            <BtnTaratic
                                disabled={page === 1}
                                onClick={() => setPage(1)}
                                color="ghost-dark"
                                size="xs"
                                className="px-2 py-1 disabled:opacity-40"
                                title="Primera página"
                            >
                                <FontAwesomeIcon icon={faAnglesLeft} />
                            </BtnTaratic>
                            <BtnTaratic
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                                color="ghost-dark"
                                size="xs"
                                className="px-2 py-1 disabled:opacity-40"
                                title="Página anterior"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </BtnTaratic>

                            <div data-ui="invoice-list-region-015" className="hidden md:flex items-center gap-1">
                                {getPageNumbers().map((pNum, idx) =>
                                    typeof pNum === 'number' ? (
                                        <BtnTaratic
                                            key={idx}
                                            onClick={() => setPage(pNum)}
                                            color={page === pNum ? 'blue' : 'ghost-dark'}
                                            size="xs"
                                            className={`min-w-[28px] px-2 py-1 font-medium ${page === pNum ? 'shadow-sm' : ''}`}
                                        >
                                            {pNum}
                                        </BtnTaratic>
                                    ) : (
                                        <span key={idx} className="px-1 text-white/40 text-xs select-none">
                                            {pNum}
                                        </span>
                                    )
                                )}
                            </div>

                            <BtnTaratic
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                                color="ghost-dark"
                                size="xs"
                                className="px-2 py-1 disabled:opacity-40"
                                title="Página siguiente"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </BtnTaratic>
                            <BtnTaratic
                                disabled={page >= totalPages}
                                onClick={() => setPage(totalPages)}
                                color="ghost-dark"
                                size="xs"
                                className="px-2 py-1 disabled:opacity-40"
                                title="Última página"
                            >
                                <FontAwesomeIcon icon={faAnglesRight} />
                            </BtnTaratic>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invoice Preview Modal */}
            {previewInvoice && (
                <div data-ui="invoice-list-region-016" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div data-ui="invoice-list-region-017" className="bg-[#11162d] rounded-3xl border border-white/20 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div data-ui="invoice-list-region-018" className="flex justify-between items-center p-4 border-b border-white/10 bg-black/75">
                            <h3 className="text-xl font-bold text-white">
                                {previewInvoice.invoice_number || t('invoices.dashboard.status.draft')} - {previewInvoice.patient_name}
                            </h3>
                            <button
                                onClick={() => setPreviewInvoice(null)}
                                className="text-white/60 hover:text-white transition-colors"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-2xl" />
                            </button>
                        </div>
                        <div data-ui="invoice-list-region-019" className="p-6 overflow-y-auto gemini-scrollbar flex-1 bg-black/20">
                            <InvoicePreview
                                issueDate={previewInvoice.issue_date}
                                invoiceType={previewInvoice.type}
                                patientName={previewInvoice.patient_name || ''}
                                lines={previewInvoice.lines || []}
                                subtotal={previewInvoice.subtotal}
                                total={previewInvoice.total}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceList;
